import type { CommerceBlogPost, Breadcrumb, Category, CollectionResult, FacetFilter, Params, Product, ProductVariant, CollectionLink, ActiveCampaign } from '@agapi/mlc-core';
import type { DistinctFacet, Entity, ProductVariantFlat, RangeFacet, Response, BlogPostFlat, CategoryFlat } from './loop54/types';
import type { Locale } from '@agapi/mlc-core/src/types/general';
import type { Loop54ClientConfig } from './loop54/client';

/**
 * Get localized campaign path based on locale
 */
const getCampaignPath = (locale: Locale): string => {
  const campaignPaths: Record<string, string> = {
    'sv': 'kampanjer',
    'en': 'campaigns',
    'es': 'promociones',
    'da': 'kampagner',
    'nb': 'kampanjer',
    'fi': 'tarjoukset'
  };

  return campaignPaths[locale.language] || 'campaigns';
};

// Get short-dated threshold from environment variable, default to 60 days
const getShortDatedThreshold = (): number => {
  // In Node.js environment, try to access the environment variable
  if (typeof process !== 'undefined' && process.env?.PUBLIC_SHORT_DATED_THRESHOLD_DAYS) {
    const threshold = parseInt(process.env.PUBLIC_SHORT_DATED_THRESHOLD_DAYS, 10);
    return isNaN(threshold) ? 60 : threshold;
  }
  // In browser environment with Vite, try to access import.meta.env
  if (typeof globalThis !== 'undefined' && 'import' in globalThis && (globalThis as any).import?.meta?.env?.PUBLIC_SHORT_DATED_THRESHOLD_DAYS) {
    const threshold = parseInt((globalThis as any).import.meta.env.PUBLIC_SHORT_DATED_THRESHOLD_DAYS, 10);
    return isNaN(threshold) ? 60 : threshold;
  }
  return 60; // Default fallback
};

const safeParseJson = (json: string) => {
  try {
    return JSON.parse(json);
  } catch (e) {
    return undefined;
  }
}

/**
 * Create campaign object from title and slug
 */
const createCampaign = (title?: string, fullSlug?: string, locale?: Locale): ActiveCampaign | undefined => {
  if (!title || !fullSlug) return undefined;

  // Extract last path segment for slug
  const slugParts = fullSlug.split('/').filter(Boolean);
  const slug = slugParts[slugParts.length - 1];

  // Extract just the campaign title (after last >)
  const titleParts = title.split('>').map(t => t.trim());
  const campaignTitle = titleParts[titleParts.length - 1];

  // Use localized campaign path if locale is provided
  const campaignPath = locale ? getCampaignPath(locale) : 'campaigns';

  return {
    title: campaignTitle,
    slug,
    url: `/${campaignPath}/${slug}`
  };
};

/**
 * Parse campaign data from JSON or pipe-separated string
 */
const parseCampaignData = (titleJson?: string, slugJson?: string, locale?: Locale): ActiveCampaign | undefined => {
  if (!titleJson || !slugJson) return undefined;

  // Try parsing as JSON first, fall back to pipe-separated
  let titles: string[] = [];
  let slugs: string[] = [];

  try {
    titles = JSON.parse(titleJson);
  } catch {
    titles = titleJson.split('|').map(t => t.trim()).filter(Boolean);
  }

  try {
    slugs = JSON.parse(slugJson);
  } catch {
    slugs = slugJson.split('|').map(s => s.trim()).filter(Boolean);
  }

  // Get last non-empty entries
  const title = titles[titles.length - 1];
  const fullSlug = slugs[slugs.length - 1];

  return createCampaign(title, fullSlug, locale);
};

/**
 * Check if product has a short-dated campaign
 */
const hasShortDatedCampaign = (
  titlesArr: string[],
  slugsArr: string[],
  locale: Locale,
  config?: Loop54ClientConfig
): boolean => {
  if (!config?.shortDatedSlugs) return false;

  const shortDatedSlug = config.shortDatedSlugs[locale.language];
  if (!shortDatedSlug) return false;

  // Check if any campaign slug contains the short-dated slug
  return slugsArr.some(slug =>
    slug.toLowerCase().includes(shortDatedSlug.toLowerCase())
  );
};

/**
 * Select the appropriate campaign with short-dated precedence
 */
const selectCampaign = (
  titlesArr: string[],
  slugsArr: string[],
  isWithinTimeThreshold: boolean,
  hasShortDatedCampaignFlag: boolean,
  locale: Locale,
  config?: Loop54ClientConfig
): ActiveCampaign | undefined => {
  // If within time threshold AND has short-dated campaign, prioritize it
  if (isWithinTimeThreshold && hasShortDatedCampaignFlag && config?.shortDatedSlugs) {
    const shortDatedSlug = config.shortDatedSlugs[locale.language];
    if (shortDatedSlug) {
      // Find matching campaign by slug (case-insensitive partial match)
      const index = slugsArr.findIndex(slug =>
        slug.toLowerCase().includes(shortDatedSlug.toLowerCase())
      );
      if (index >= 0 && titlesArr[index]) {
        return createCampaign(titlesArr[index], slugsArr[index], locale);
      }
      // If no matching campaign exists but product should be short-dated, synthesize one
      const shortDatedTitle = locale.language === 'sv' ? 'Fyndhörna' : 'Short-dated';
      const campaignPath = getCampaignPath(locale);
      return {
        title: shortDatedTitle,
        slug: shortDatedSlug,
        url: `/${campaignPath}/${shortDatedSlug}`
      };
    }
  }

  // Fallback: current behavior (last campaign)
  if (titlesArr.length && slugsArr.length) {
    const lastTitle = titlesArr[titlesArr.length - 1];
    const lastSlug = slugsArr[slugsArr.length - 1];
    return createCampaign(lastTitle, lastSlug, locale);
  }

  return undefined;
};

/**
 * Calculate days until best before date
 */
const calculateDaysUntilBestBefore = (bestBeforeDate?: string): number | undefined => {
  if (!bestBeforeDate) return undefined;

  try {
    const bestBefore = new Date(bestBeforeDate);

    // Validate the parsed date
    if (isNaN(bestBefore.getTime())) {
      return undefined;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    bestBefore.setHours(0, 0, 0, 0);

    const diffTime = bestBefore.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  } catch {
    return undefined;
  }
};

/**
 * Calculate days until campaign end date (handles UTC conversion)
 */
const calculateDaysUntilCampaignEnd = (campaignEndDate?: string): number | undefined => {
  if (!campaignEndDate) return undefined;

  try {
    // Campaign end date is in UTC format like "2025-09-02 21:59:59"
    // Convert to proper ISO format for parsing
    const isoDate = campaignEndDate.replace(' ', 'T') + 'Z';
    const campaignEnd = new Date(isoDate);

    // Validate the parsed date
    if (isNaN(campaignEnd.getTime())) {
      return undefined;
    }

    const now = new Date();

    const diffTime = campaignEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  } catch {
    return undefined;
  }
};

/**
 * Determine if product is short-dated based on threshold
 */
const isShortDated = (daysUntilBestBefore?: number, threshold: number = 60): boolean => {
  if (daysUntilBestBefore === undefined) return false;
  return daysUntilBestBefore >= 0 && daysUntilBestBefore <= threshold;
};

/**
 * Try to parse a JSON array; fall back to pipe-separated string
 */
const tryParseArray = (value?: string): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.map((v) => String(v))
      : String(value).split('|').map((s) => s.trim()).filter(Boolean);
  } catch {
    return String(value).split('|').map((s) => s.trim()).filter(Boolean);
  }
};

type ObjectWithStringKey<T, K extends string> = T & { [P in K]: string };

interface Tree {
  [key: string]: Tree;
}

export const getLeaves = <T, K extends string>(objects: ObjectWithStringKey<T, K>[], key: K, delimiter = '/'): ObjectWithStringKey<T, K>[] => {
  const tree: Tree = {
    nodes: {}
  };
  const refs: Record<string, ObjectWithStringKey<T, K>> = {};

  // Build the tree
  for (const obj of objects) {
    const path = obj[key].split(delimiter).map(p => p.trim()).join(delimiter);

    path.split(delimiter).reduce<Tree>((acc, path) => {
      if (!acc?.nodes[path]) {
        acc.nodes[path] = {
          parent: acc,
          nodes: {}
        };
      }
      return acc.nodes[path];
    }, tree);

    refs[path] = obj;
  }

  const leafs: string[] = [];

  // Helper function to check if node is a leaf
  const isLeaf = (node: Tree): boolean => {
    return Object.keys(node.nodes).length === 0;
  }

  const isMultiBranch = (node: Tree): boolean => {
    return Object.keys(node.nodes).length > 1;
  }

  const isRootBranch = (node: Tree): boolean => {
    return node.parent === undefined;
  }

  // Helper function to process nodes and find leaves
  const processNode = (node: Tree, currentPath: string[] = []): void => {

    if (isLeaf(node)) { // Pure leaf
      leafs.push(currentPath.join(delimiter));
      return;
    } else if (isMultiBranch(node) && !isRootBranch(node)) { // Multi branch that is not the root branch
      leafs.push(currentPath.join(delimiter));
      // ... continue processing the branch
    }

    for (const [key, value] of Object.entries(node.nodes)) {
      processNode(value, [...currentPath, key]);
    }
  }

  processNode(tree);

  // console.log('leafs', leafs);

  return leafs.map(leaf => refs[leaf]).filter(c => c);
}

const parseBreadcrumbs = (breadcrumbs?: string): Breadcrumb[] => {
  const crumbs = (breadcrumbs?.trim() === '{}' ? [] : JSON.parse(breadcrumbs?.trim() ?? '[]')) as {
    id: string;
    slug: string;
    title: string;
    full_slug: string;
    full_title: string;
  }[];

  return crumbs.map<Breadcrumb>((crumb) => ({
    id: crumb.id,
    slug: crumb.slug,
    title: crumb.title,
    fullSlug: crumb.full_slug,
    fullTitle: crumb.full_title
  }));
};

const parseCollectionLinks = (product: ProductVariantFlat): CollectionLink[] => {
  if (product.content_json) {
    const content = JSON.parse(product.content_json) as {
      id: number;
      slug: string;
      title: string;
      full_slug: string;
      full_title: string
    }[];

    const collections = content
      .map<CollectionLink>((c) => {
        return {
          id: c.id.toString(),
          slug: c.slug,
          title: c.title,
          fullSlug: c.full_slug.replace(/^\/(glimja|happygreen)/, ''),
          fullTitle: c.full_title
        }
      })
      .filter(c => c && c?.title && c?.fullSlug);

    return getLeaves(collections, 'fullSlug');
  }
  return [];
}

export const transformItem = (entity: Entity): ProductVariantFlat => {
  // Log complete raw Loop54 entity data
  // eslint-disable-next-line no-console
  console.log(`[Loop54 Raw Entity] ${entity.id}:`, {
    type: entity.type,
    id: entity.id,
    attributes: entity.attributes?.map(attr => ({
      name: attr.name,
      type: attr.type,
      values: attr.values
    })) || []
  });

  return entity.attributes?.reduce<Record<string, any>>((obj, attr) => {
    obj[attr.name] = attr.values.length > 1 ? attr.values : attr.values[0];
    return obj;
  }, {}) as ProductVariantFlat;
};

export const transformItems = (entities: Entity[]) => {
  return entities.map((e) => transformItem(e));
};

export const transformFaucets = (faucets: Array<DistinctFacet | RangeFacet>): FacetFilter[] => {
  return faucets.map((f) => {
    if (f.type === 'distinct') {
      return {
        label: f.name,
        type: f.type,
        items: getLeaves(f.items.map((i) => ({
          label: i.item as string,
          count: i.count,
          selected: i.selected
        })), 'label', '>'),
        count: f.items.length
      };
    }

    return {
      label: f.name,
      type: f.type,
      item: {
        min: f.min as number,
        max: f.max as number,
        selectedMin: f.selectedMin as number,
        selectedMax: f.selectedMax as number
      }
    };
  });
};

export const getLocalizedValue = <T>(key: string, obj: Record<string, any>, locale: Locale): T => {
  // console.log(`${key}_${locale.country}`, obj);
  return obj[`${key}_${locale.country}`] as T;
};

const transformBrand = (product: {brand_name?: string, brand_slug?: string}) => {
  const name = product.brand_name?.split('|').pop()?.trim() || product.brand_name || '';
  const handle = product.brand_slug?.split('|').pop()?.trim() || product.brand_slug || '';
  return {
    name,
    handle,
  };
};

export const transformProductVariant = (product: ProductVariantFlat, locale: Locale, config?: Loop54ClientConfig): ProductVariant => {
  // Use real API data
  const campaignTitleJson = product.campaign_full_title_json;
  const campaignSlugJson = product.campaign_full_slug_json;
  const campaignEndDate = product.campaign_end_date;
  const bestBeforeDate = product.best_before_own_enkoping;

  // Parse all campaign data
  const titlesArr = tryParseArray(campaignTitleJson);
  const slugsArr = tryParseArray(campaignSlugJson);

  // Calculate time-based short-dated status
  const daysUntilBestBefore = bestBeforeDate ? calculateDaysUntilBestBefore(bestBeforeDate) : undefined;
  const shortDatedThreshold = getShortDatedThreshold();
  const isWithinTimeThreshold = daysUntilBestBefore !== undefined ? isShortDated(daysUntilBestBefore, shortDatedThreshold) : false;

  // Calculate campaign end date
  const daysUntilCampaignEnd = campaignEndDate ? calculateDaysUntilCampaignEnd(campaignEndDate) : undefined;

  // Check if product has short-dated campaign
  const hasShortDatedCampaignFlag = hasShortDatedCampaign(titlesArr, slugsArr, locale, config);

  // Product is only considered short-dated if BOTH conditions are met
  const shortDated = isWithinTimeThreshold && hasShortDatedCampaignFlag;

  // Select campaign with precedence logic
  const activeCampaign = selectCampaign(titlesArr, slugsArr, isWithinTimeThreshold, hasShortDatedCampaignFlag, locale, config);

  return {
    id: product.id,
    name: product.name,
    sku: product.sku,
    handle: encodeURIComponent(product.handle),
    description: {
      long: product.long_description,
      short: product.short_description
    },
    breadcrumbs: parseBreadcrumbs(product.breadcrumb),
    categories: product.categories,
    stock: product.stock,
    images: product.images ? JSON.parse(product.images) : [],
    brand: transformBrand(product),
    price: {
      amount: getLocalizedValue('price_base', product, locale) ?? 0,
      discount: getLocalizedValue('price_discount', product, locale) ?? 0,
      vat: getLocalizedValue('price_vat', product, locale) ?? 0,
      currency: getLocalizedValue('currency', product, locale)
    },
    collections: parseCollectionLinks(product),
    meta: {
      canonical: product.canonical,
      title: product.meta_title,
      description: product.meta_description
    },
    relatedBlogPosts: product.related_blog_posts?.replace(/\|/g, ','),
    languageSlugs: product.language_slugs ? safeParseJson(product.language_slugs) : undefined,
    languageFullSlugs: product.language_slugs ? transformLanguageFullSlugs(safeParseJson(product.language_slugs)) : undefined,
    // New campaign and short-dated fields
    isShortDated: shortDated,
    activeCampaign,
    bestBeforeDate,
    daysUntilBestBefore,
    campaignEndDate,
    daysUntilCampaignEnd
  }
};

export const transformLanguageFullSlugs = (languageSlugs?: Record<string, string>) => {
  if (!languageSlugs) return undefined;

  const pathMappings = {
    sv: '/produkt/',
    en: '/en/product/',
    fi: '/fi/tuote/',
    nb: '/nb/produkt/',
    da: '/da/produkt/',
    es: '/es/producto/'
  };

  const result: Record<string, string> = {};

  for (const [lang, path] of Object.entries(pathMappings)) {
    if (languageSlugs[lang]) {
      result[lang] = `${path}${languageSlugs[lang]}`;
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

export const transformProduct = (product: ProductVariantFlat, locale: Locale, config?: Loop54ClientConfig): Product => {
  // Use real API data
  const campaignTitleJson = product.campaign_full_title_json;
  const campaignSlugJson = product.campaign_full_slug_json;
  const campaignEndDate = product.campaign_end_date;
  const bestBeforeDate = product.best_before_own_enkoping;

  // Parse all campaign data
  const titlesArr = tryParseArray(campaignTitleJson);
  const slugsArr = tryParseArray(campaignSlugJson);

  // Calculate time-based short-dated status
  const daysUntilBestBefore = bestBeforeDate ? calculateDaysUntilBestBefore(bestBeforeDate) : undefined;
  const shortDatedThreshold = getShortDatedThreshold();
  const isWithinTimeThreshold = daysUntilBestBefore !== undefined ? isShortDated(daysUntilBestBefore, shortDatedThreshold) : false;

  // Calculate campaign end date
  const daysUntilCampaignEnd = campaignEndDate ? calculateDaysUntilCampaignEnd(campaignEndDate) : undefined;

  // Check if product has short-dated campaign
  const hasShortDatedCampaignFlag = hasShortDatedCampaign(titlesArr, slugsArr, locale, config);

  // Product is only considered short-dated if BOTH conditions are met
  const shortDated = isWithinTimeThreshold && hasShortDatedCampaignFlag;

  // Select campaign with precedence logic
  const activeCampaign = selectCampaign(titlesArr, slugsArr, isWithinTimeThreshold, hasShortDatedCampaignFlag, locale, config);



  return {
    id: product.id,
    brand: transformBrand(product),
    description: {
      long: product.long_description,
      short: product.short_description
    },
    breadcrumbs: parseBreadcrumbs(product.breadcrumb),
    categories: product.categories,
    handle: encodeURIComponent(product.handle),
    name: product.name,
    ingredients: product.ingredients,
    nutrition: product['nutritional-content'],
    variants: [ transformProductVariant(product, locale, config) ],
    collections: parseCollectionLinks(product),
    meta: {
      canonical: product.canonical,
      title: product.meta_title,
      description: product.meta_description
    },
    // TODO: Replace with related_blog_posts instead of related_blog_posts_GL
    relatedBlogPosts: product.related_blog_posts?.replace(/\|/g, ','),
    languageSlugs: product.language_slugs ? safeParseJson(product.language_slugs) : undefined,
    languageFullSlugs: product.language_slugs ? transformLanguageFullSlugs(safeParseJson(product.language_slugs)) : undefined,
    // New campaign and short-dated fields
    isShortDated: shortDated,
    activeCampaign,
    bestBeforeDate,
    daysUntilBestBefore,
    campaignEndDate,
    daysUntilCampaignEnd
  };
};

export const transformProducts = (products: ProductVariantFlat[], locale: Locale, config?: Loop54ClientConfig): Product[] => {
  return products.map((p) => transformProduct(p, locale, config));
};

export const transformProductVariants = (products: ProductVariantFlat[], locale: Locale, config?: Loop54ClientConfig): ProductVariant[] => {
  return products.filter(p => p.id !== undefined).map((p) => transformProductVariant(p, locale, config));
};

export const transformCollectionResult = <T, CUSTOM_DATA>(response: Response<CUSTOM_DATA>, transformer: (response: Response<CUSTOM_DATA>) => T[], params: Params): CollectionResult<T> => {
  return {
    items: transformer(response),
    facetFilters: transformFaucets(response.results.facets),
    count: response.results.count,
    limit: params.limit,
    offset: params.offset ?? 0,
    sortBy: params.sortBy
  };
};

export const transformBlogPostFlat = (blogPost: BlogPostFlat): CommerceBlogPost => {
  const getAttribute = (name: string) => {
    const attr = blogPost.attributes.find(a => a.name === `blog_${name}`);
    return attr?.values[0];
  };

  const getAttributes = (name: string) => {
    const attr = blogPost.attributes.find(a => a.name === `blog_${name}`);
    return attr?.values ?? [];
  };

  return {
    id: blogPost.id,
    slug: getAttribute('slug') ?? '',
    title: getAttribute('title') ?? '',
    content: getAttribute('content') ?? '',
    categories: getAttributes('categoryname'),
    featuredImageUrl: getAttribute('featuredImageUrl') ?? '',
    publishedAt: getAttribute('publishedAt') ?? ''
  };
};


export const transformBlogPosts = (blogPosts: BlogPostFlat[]): CommerceBlogPost[] => {
  return blogPosts.map((p) => transformBlogPostFlat(p));
};

export const transformCategory = (category: CategoryFlat): Category => {
  const getAttribute = (name: string) => {
    const attr = category.attributes.find(a => a.name === `category_${name}`);
    return attr?.values[0];
  };

  return {
    id: category.id,
    name: getAttribute('name') ?? '',
    path: getAttribute('path') ?? '',
    description: getAttribute('description') ?? ''
  };
};


export const transformCategories = (categories: CategoryFlat[]): Category[] => {
  return categories.map((p) => transformCategory(p));
};

export const transformBrandCollection = (brand: CategoryFlat): Category => {
  const getAttribute = (name: string) => {
    const attr = brand.attributes.find(a => a.name === `brand_${name}`);
    return attr?.values[0];
  };

  return {
    id: brand.id,
    name: getAttribute('name') ?? '',
    path: getAttribute('path') ?? '',
    description: getAttribute('description') ?? ''
  };
};

export const transformBrands = (brands: CategoryFlat[]): Category[] => {
  return brands.map((p) => transformBrandCollection(p));
};